import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { API, colors } from "../shared/constants";
import { styles } from "../shared/styles";
import { isArchivedSite } from "../shared/utils";

export function StatisticsPage() {
  const [inspections, setInspections] = useState([]);
  const [sites, setSites] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("year");

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/inspections/`),
      axios.get(`${API}/sites/`),
      axios.get(`${API}/accounts/`),
    ])
      .then(([inspRes, sitesRes, accountsRes]) => {
        setInspections(inspRes.data);
        setSites(sitesRes.data);
        setAccounts(accountsRes.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const now = new Date();
  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    if (period === "week") {
      start.setDate(start.getDate() - 6);
    } else if (period === "month") {
      start = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (period === "year") {
      start = new Date(now.getFullYear(), 0, 1);
    } else if (period === "last_year") {
      start = new Date(now.getFullYear() - 1, 0, 1);
      end.setFullYear(now.getFullYear() - 1, 11, 31);
    }
    return { start, end };
  };
  const { start, end } = getDateRange();
  const completedInspections = inspections.filter((i) => {
    if (i.status === "in_progress") return false;
    const d = new Date(i.date);
    return d >= start && d <= end;
  });
  const calcValue = (inspList) =>
    inspList.reduce((sum, insp) => {
      const site = sites.find((s) => s.id === insp.site);
      if (!site) return sum;
      if (insp.inspection_type === "monthly")
        return sum + (parseFloat(site.monthly_fee) || 0);
      if (insp.inspection_type === "bi_annual")
        return sum + (parseFloat(site.bi_annual_fee) || 0);
      if (insp.inspection_type === "annual")
        return sum + (parseFloat(site.annual_fee) || 0);
      return sum;
    }, 0);

  const buildChartData = () => {
    if (period === "week") {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const label = d.toLocaleDateString("en-AU", {
          weekday: "short",
          day: "numeric",
        });
        const dayInspections = completedInspections.filter(
          (insp) => new Date(insp.date).toDateString() === d.toDateString(),
        );
        days.push({
          label,
          inspections: dayInspections.length,
          monthly: dayInspections.filter((i) => i.inspection_type === "monthly")
            .length,
          bi_annual: dayInspections.filter(
            (i) => i.inspection_type === "bi_annual",
          ).length,
          annual: dayInspections.filter((i) => i.inspection_type === "annual")
            .length,
          value: calcValue(dayInspections),
        });
      }
      return days;
    } else if (period === "month") {
      const daysInMonth = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        0,
      ).getDate();
      const days = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(now.getFullYear(), now.getMonth(), i);
        if (d > now) break;
        const dayInspections = completedInspections.filter((insp) => {
          const id = new Date(insp.date);
          return id.getDate() === i && id.getMonth() === now.getMonth();
        });
        days.push({
          label: `${i}`,
          inspections: dayInspections.length,
          monthly: dayInspections.filter((i) => i.inspection_type === "monthly")
            .length,
          bi_annual: dayInspections.filter(
            (i) => i.inspection_type === "bi_annual",
          ).length,
          annual: dayInspections.filter((i) => i.inspection_type === "annual")
            .length,
          value: calcValue(dayInspections),
        });
      }
      return days;
    } else {
      const year =
        period === "last_year" ? now.getFullYear() - 1 : now.getFullYear();
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return months.map((label, i) => {
        const monthInspections = completedInspections.filter((insp) => {
          const d = new Date(insp.date);
          return d.getMonth() === i && d.getFullYear() === year;
        });
        return {
          label,
          inspections: monthInspections.length,
          monthly: monthInspections.filter(
            (i) => i.inspection_type === "monthly",
          ).length,
          bi_annual: monthInspections.filter(
            (i) => i.inspection_type === "bi_annual",
          ).length,
          annual: monthInspections.filter((i) => i.inspection_type === "annual")
            .length,
          value: calcValue(monthInspections),
        };
      });
    }
  };

  const chartData = loading ? [] : buildChartData();
  const totalValue = chartData.reduce((s, d) => s + d.value, 0);
  const totalInspections = chartData.reduce((s, d) => s + d.inspections, 0);
  // Active only, so this matches the home page and the accounts list.
  const totalPortfolio = sites
    .filter((site) => !isArchivedSite(site))
    .reduce((s, site) => s + (parseFloat(site.annual_value) || 0), 0);
  const activeAccounts = accounts.filter((a) => a.status === "active").length;
  const activeSites = sites.filter((s) => s.status === "active").length;
  const periodLabel = {
    week: "This Week",
    month: "This Month",
    year: "This Year",
    last_year: "Last Year",
  }[period];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: "8px",
            padding: "12px 16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            fontSize: "13px",
          }}
        >
          <div
            style={{
              fontWeight: "700",
              marginBottom: "6px",
              color: colors.text,
            }}
          >
            {label}
          </div>
          {payload.map((p) => (
            <div key={p.name} style={{ color: p.color, marginBottom: "2px" }}>
              {p.name}:{" "}
              <span style={{ fontWeight: "600" }}>
                {p.name === "Value ($)"
                  ? `$${parseFloat(p.value).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`
                  : p.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const ChartCard = ({ title, children, subtitle }) => (
    <div
      style={{
        backgroundColor: colors.white,
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        marginBottom: "20px",
      }}
    >
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{ fontSize: "16px", fontWeight: "700", color: colors.text }}
        >
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: "13px", color: "#999", marginTop: "2px" }}>
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Statistics</div>
        <div style={{ display: "flex", gap: "8px" }}>
          {[
            ["week", "This Week"],
            ["month", "This Month"],
            ["year", "This Year"],
            ["last_year", "Last Year"],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setPeriod(val)}
              style={{
                padding: "8px 16px",
                borderRadius: "6px",
                border: `2px solid ${period === val ? colors.red : colors.border}`,
                backgroundColor: period === val ? colors.red : colors.white,
                color: period === val ? colors.white : colors.text,
                fontWeight: "600",
                fontSize: "13px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div style={styles.content}>
        {loading ? (
          <div style={styles.loading}>Loading statistics...</div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "16px",
                marginBottom: "24px",
              }}
            >
              {[
                {
                  label: `Inspections — ${periodLabel}`,
                  value: totalInspections,
                  color: colors.blue,
                  sub: "Completed",
                },
                {
                  label: `Value Delivered — ${periodLabel}`,
                  value: `$${totalValue.toLocaleString("en-AU", { minimumFractionDigits: 2 })}`,
                  color: colors.green,
                  sub: "From completed services",
                },
                {
                  label: "Value of Contracts",
                  value: `$${totalPortfolio.toLocaleString("en-AU", { minimumFractionDigits: 2 })}`,
                  color: colors.orange,
                  sub: "Annual contract value",
                },
                {
                  label: "Active Sites",
                  value: activeSites,
                  color: colors.darkGrey,
                  sub: `${activeAccounts} accounts`,
                },
              ].map(({ label, value, color, sub }) => (
                <div
                  key={label}
                  style={{
                    backgroundColor: colors.white,
                    borderRadius: "10px",
                    padding: "20px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    borderLeft: `4px solid ${color}`,
                  }}
                >
                  <div style={{ fontSize: "26px", fontWeight: "700", color }}>
                    {value}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: colors.text,
                      marginTop: "4px",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#999",
                      marginTop: "2px",
                    }}
                  >
                    {sub}
                  </div>
                </div>
              ))}
            </div>
            <ChartCard
              title="Value of Services Delivered"
              subtitle={`Revenue from completed inspections — ${periodLabel}`}
            >
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="valueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={colors.green}
                        stopOpacity={0.2}
                      />
                      <stop
                        offset="95%"
                        stopColor={colors.green}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    name="Value ($)"
                    stroke={colors.green}
                    strokeWidth={2.5}
                    fill="url(#valueGrad)"
                    dot={{ fill: colors.green, r: 3 }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard
              title="Inspections Completed"
              subtitle={`Total services delivered by type — ${periodLabel}`}
            >
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                  <defs>
                    <linearGradient
                      id="monthlyGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={colors.blue}
                        stopOpacity={0.15}
                      />
                      <stop
                        offset="95%"
                        stopColor={colors.blue}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="biannualGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={colors.orange}
                        stopOpacity={0.15}
                      />
                      <stop
                        offset="95%"
                        stopColor={colors.orange}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient id="annualGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={colors.green}
                        stopOpacity={0.15}
                      />
                      <stop
                        offset="95%"
                        stopColor={colors.green}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#999" }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: "12px", paddingTop: "12px" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="monthly"
                    name="Monthly"
                    stroke={colors.blue}
                    strokeWidth={2}
                    fill="url(#monthlyGrad)"
                    dot={{ fill: colors.blue, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="bi_annual"
                    name="Bi-Annual"
                    stroke={colors.orange}
                    strokeWidth={2}
                    fill="url(#biannualGrad)"
                    dot={{ fill: colors.orange, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="annual"
                    name="Annual"
                    stroke={colors.green}
                    strokeWidth={2}
                    fill="url(#annualGrad)"
                    dot={{ fill: colors.green, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard
              title="Total Annual Contract Portfolio"
              subtitle="Combined value of all active site contracts"
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "16px",
                }}
              >
                {[
                  {
                    label: "Monthly Contracts",
                    value: sites.filter((s) => s.monthly_fee).length,
                    sub: `$${sites.reduce((s, site) => s + (parseFloat(site.monthly_fee) || 0) * (site.monthly_fee_months || 12), 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })} /yr`,
                    color: colors.blue,
                  },
                  {
                    label: "Bi-Annual Contracts",
                    value: sites.filter((s) => s.bi_annual_fee).length,
                    sub: `$${sites.reduce((s, site) => s + (parseFloat(site.bi_annual_fee) || 0), 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })} /yr`,
                    color: colors.orange,
                  },
                  {
                    label: "Annual Contracts",
                    value: sites.filter((s) => s.annual_fee).length,
                    sub: `$${sites.reduce((s, site) => s + (parseFloat(site.annual_fee) || 0), 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })} /yr`,
                    color: colors.green,
                  },
                ].map(({ label, value, sub, color }) => (
                  <div
                    key={label}
                    style={{
                      backgroundColor: colors.lightGrey,
                      borderRadius: "10px",
                      padding: "20px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "36px", fontWeight: "700", color }}>
                      {value}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: colors.text,
                        marginTop: "4px",
                      }}
                    >
                      {label}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: colors.green,
                        fontWeight: "600",
                        marginTop: "4px",
                      }}
                    >
                      {sub}
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  backgroundColor: "#f0faf0",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colors.text,
                  }}
                >
                  Total Annual Portfolio Value
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: colors.green,
                  }}
                >
                  $
                  {totalPortfolio.toLocaleString("en-AU", {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>
            </ChartCard>
          </>
        )}
      </div>
    </div>
  );
}
